import React from "react"
import types from "prop-types"
import { useDispatch, useSelector } from "react-redux"
import {
  setFormQuestionType,
  isFocusOnQuestion,
  getFormIsSubform,
  getFormQuestionHasLockedOption,
  getFormQuestionType,
  getFormQuestionUsedByDateDifferenceQuestion,
  getFormQuestionUsedByNumberCalculationQuestion,
  getFormQuestionUsedByTimeDifferenceQuestion,
  hasQuestionBeenAnsweredByIndex,
} from "reduxSlices/formBuilderSlice"
import { IconSelect } from "shared/selects"
import SvgSpriteIcon from "shared/SvgSpriteIcon"
import Tooltip, { BOTTOM_LEFT } from "shared/Tooltip"
import { formElementTypeOptions, questionTypeIcon, questionTypeLabel } from "Forms/FormElementTypes"

const ANSWERED_TOOLTIP_TEXT = "Type cannot be changed if question has been answered"
const LOCKED_OPTION_TOOLTIP_TEXT = "Type cannot be changed if it has a locked option"
const DATE_DIFFERENCE_TOOLTIP_TEXT = "Type cannot be changed if a date difference question measures using this question"
const NUMBER_CALCULATION_TOOLTIP_TEXT = "Type cannot be changed if a number calculation question uses this question"
const TIME_DIFFERENCE_TOOLTIP_TEXT = "Type cannot be changed if a time difference question measures using this question"

const renderContentIcon = ({ icon }) => <SvgSpriteIcon iconName={`${icon}-dark`} />
const renderItemIcon = ({ icon }) => <SvgSpriteIcon iconName={icon} />

const QuestionType = ({ sectionIndex, questionIndex }) => {
  const dispatch = useDispatch()

  const formIsSubform = useSelector(getFormIsSubform)
  const focusIsOnQuestion = useSelector(isFocusOnQuestion({ sectionIndex, questionIndex }))

  const questionHasBeenAnswered = useSelector(hasQuestionBeenAnsweredByIndex({ sectionIndex, questionIndex }))
  const questionHasLockedOption = useSelector(getFormQuestionHasLockedOption({ sectionIndex, questionIndex }))
  const questionUsedByDateDifference = useSelector(getFormQuestionUsedByDateDifferenceQuestion({ sectionIndex, questionIndex }))
  const questionUsedByNumberCalculation = useSelector(
    getFormQuestionUsedByNumberCalculationQuestion({ sectionIndex, questionIndex }),
  )
  const questionUsedByTimeDifference = useSelector(getFormQuestionUsedByTimeDifferenceQuestion({ sectionIndex, questionIndex }))

  const lockReasons = [
    { locked: questionHasBeenAnswered, tooltipText: ANSWERED_TOOLTIP_TEXT },
    { locked: questionHasLockedOption, tooltipText: LOCKED_OPTION_TOOLTIP_TEXT },
    { locked: questionUsedByDateDifference, tooltipText: DATE_DIFFERENCE_TOOLTIP_TEXT },
    { locked: questionUsedByNumberCalculation, tooltipText: NUMBER_CALCULATION_TOOLTIP_TEXT },
    { locked: questionUsedByTimeDifference, tooltipText: TIME_DIFFERENCE_TOOLTIP_TEXT },
  ]

  const activeLockReason = lockReasons.find(({ locked }) => locked)
  const questionTypeIsLocked = Boolean(activeLockReason)
  const questionTypeLockDescription = activeLockReason?.tooltipText ?? ""

  const questionType = useSelector(getFormQuestionType({ sectionIndex, questionIndex }))

  const updateQuestionType = (newType) => {
    dispatch(setFormQuestionType({ sectionIndex, questionIndex, newType }))
  }

  if (!focusIsOnQuestion) return null

  if (questionTypeIsLocked) {
    return (
      // The w-min wrapper sizes this element to the tab it contains.
      // Without it, the tooltip is misaligned.
      <div className="w-min">
        <Tooltip text={questionTypeLockDescription} position={BOTTOM_LEFT}>
          <div className="form-builder-question-type-tab">
            <SvgSpriteIcon iconName={`${questionTypeIcon({ questionType })}-dark`} />
            <span>{questionTypeLabel({ questionType })}</span>
          </div>
        </Tooltip>
      </div>
    )
  }

  return (
    // The w-min wrapper sizes this element to the select it contains.
    // Without it, the select's focus ring spans the full row.
    <div className="w-min">
      <IconSelect
        className="form-builder-question-type-select"
        hasBaseInputStyle={false}
        options={formElementTypeOptions(formIsSubform)}
        value={questionType}
        onChange={updateQuestionType}
        renderContentIcon={renderContentIcon}
        renderItemIcon={renderItemIcon}
        backspaceDelete={false}
        selectLabel="select question type"
      />
    </div>
  )
}

QuestionType.propTypes = {
  sectionIndex: types.number.isRequired,
  questionIndex: types.number.isRequired,
}

export default QuestionType
